import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Plane, CalendarRange, ArrowRight, Route, CircleCheck } from '@/components/icons';
import { EmptyState } from './EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { useTripGetMyTrips } from '../lib/hooks';
import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { TripCard } from './TripCard';
import dayjs from 'dayjs';
import { TopBarBackButton } from './topbar/TopBarBackButton';
import { AppLayout } from './AppLayout';
import { DesktopPageHeader } from './desktop/DesktopPageHeader';
import { useIsDesktop } from './ui/use-mobile';

import { TripStatusBadge } from './TripStatusBadge';
import { toStatusBadge, type TripState } from '@/lib/tripState';
import { hapticLight } from '@/lib/haptics';

type TripStatusBadge = ReturnType<typeof toStatusBadge>;
type TripTab = 'scheduled' | 'complete';

const TAB_STATUS_MAP: Record<TripTab, TripStatusBadge[]> = {
  scheduled: ['scheduled'],
  complete: ['complete'],
};

// Unique aircraft registrations across a trip's flights (aircraft is per-flight)
function uniqueTails(trip: { legs?: { aircraft?: { registration: string } | null }[] }): string[] {
  return [
    ...new Set(
      (trip.legs || []).map((leg) => leg.aircraft?.registration).filter((r): r is string => !!r)
    ),
  ];
}

export function MyTripList() {
  const [activeTab, setActiveTab] = useState<TripTab>('scheduled');
  const { data: currentUser } = useCurrentCrewMember();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const { data: trips = [], isPending: loading } = useTripGetMyTrips(currentUser?.id);

  const tripsWithStatus = trips.map((trip) => ({
    ...trip,
    status: toStatusBadge(trip.tripState as TripState),
  }));

  // Hero trip: first scheduled
  const heroTrip =
    tripsWithStatus.find((t) => t.status === 'scheduled') ||
    null;

  // Compute hero trip stats from legs
  const heroStats = heroTrip
    ? (() => {
        const legs = heroTrip.legs || [];
        const uniqueCrewIds = new Set(legs.flatMap((leg: any) => leg.crew?.map((c: any) => c.id) || []));
        const uniquePaxIds = new Set(legs.flatMap((leg: any) => leg.passengers?.map((p: any) => p.id) || []));
        const uniqueAirportIds = new Set(
          legs.flatMap((leg: any) => [leg.departureAirportIcao, leg.arrivalAirportIcao].filter(Boolean))
        );
        return {
          flightsCount: legs.length,
          airportsCount: uniqueAirportIds.size,
          crewCount: uniqueCrewIds.size,
          paxCount: uniquePaxIds.size,
        };
      })()
    : null;

  const filteredTrips = tripsWithStatus.filter((trip) =>
    TAB_STATUS_MAP[activeTab].includes(trip.status)
  );

  return (
    <AppLayout
      topBar={
        isDesktop ? (
          <div className="px-gutter pt-8 pb-2">
            <DesktopPageHeader
              title="My Assigned Trips"
              breadcrumb={
                <span>
                  <Link to="/schedule" className="hover:underline">
                    My Schedule
                  </Link>
                  <span className="text-content-primary-subtle"> / My Assigned Trips</span>
                </span>
              }
            />
          </div>
        ) : (
          <TopBarBackButton title="My Assigned Trips" />
        )
      }
      className="pt-4"
    >
      <div className="flex flex-col gap-6 flex-1">

        {loading ? (
          <>
            {/* Hero card */}
            <Skeleton className="h-44 w-full rounded-2xl" />

            {/* Tabs */}
            <Skeleton className="h-9 w-full rounded-lg" />

            {/* Trip list */}
            <div className="flex flex-col gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Card key={i} className="flex flex-col gap-1 px-4 py-2">
                  <div className="flex items-center justify-between py-2">
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-6 w-16 rounded-full" />
                  </div>
                  <div className="border-t border-border-L1 flex items-center justify-between pb-2 pt-3">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-28" />
                  </div>
                </Card>
              ))}
            </div>
          </>
        ) : trips.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <EmptyState
              icon={Route}
              title="No trips yet"
              description="Your schedule will appear here once you're assigned to a trip."
            />
          </div>
        ) : (
          <>
            {/* Hero Card */}
            {heroTrip && heroStats && (
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => {
                    hapticLight()
                    navigate({ to: '/schedule/$tripId', params: { tripId: heroTrip.id } })
                  }}
                  className="block w-full text-left active:scale-[0.98] transition-transform"
                >
                  <TripCard
                    tripName={heroTrip.tripName}
                    aircraftRegistrations={uniqueTails(heroTrip)}
                    startDate={heroTrip.tripStartDate}
                    endDate={heroTrip.tripEndDate}
                    tripState={heroTrip.tripState as TripState}
                    flightsCount={heroStats.flightsCount}
                    airportsCount={heroStats.airportsCount}
                    crewCount={heroStats.crewCount}
                    paxCount={heroStats.paxCount}
                  />
                </button>
              </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TripTab)}>
              <TabsList className="w-full">
                <TabsTrigger value="scheduled" className="flex-1">Scheduled</TabsTrigger>
                <TabsTrigger value="complete" className="flex-1">Complete</TabsTrigger>
              </TabsList>

              {(['scheduled', 'complete'] as TripTab[]).map((tab) => (
                <TabsContent key={tab} value={tab}>
                  {filteredTrips.length === 0 && tab === 'scheduled' ? (
                    <div className="flex items-center justify-center py-16">
                      <EmptyState
                        icon={Route}
                        title="No scheduled trips"
                        description="Trips assigned to you will appear here once they're scheduled."
                      />
                    </div>
                  ) : filteredTrips.length === 0 && tab === 'complete' ? (
                    <div className="flex items-center justify-center py-16">
                      <EmptyState
                        icon={CircleCheck}
                        title="No completed trips"
                        description="Trips you've flown will show up here once they're complete."
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {filteredTrips.map((trip) => {
                        const startDate = trip.tripStartDate ? dayjs(trip.tripStartDate).format('D MMM') : 'N/A';
                        const endDate = trip.tripEndDate ? dayjs(trip.tripEndDate).format('D MMM') : 'N/A';

                        return (
                          <Card
                            key={trip.id}
                            interactive
                            onClick={() => navigate({ to: '/schedule/$tripId', params: { tripId: trip.id } })}
                            className="flex flex-col gap-1 px-4 py-2 cursor-pointer"
                          >
                            {/* Card Header */}
                            <div className="flex items-center justify-between py-2">
                              <span className="font-semibold text-base text-content-primary-bold">
                                {trip.tripName}
                              </span>
                              <TripStatusBadge status={trip.status} />
                            </div>

                            {/* Card Content */}
                            <div className="border-t border-border-L1 flex items-center justify-between pb-2 pt-3">
                              <div className="flex items-center gap-2 py-0.5">
                                <Plane className="size-5 text-content-brand-bold" />
                                <span className="font-medium text-sm text-content-primary-bold">
                                  {uniqueTails(trip).join(', ') || 'N/A'}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <CalendarRange className="size-5 text-content-brand-bold" />
                                <span className="text-sm text-content-primary-bold">{startDate}</span>
                                <ArrowRight className="size-3 text-border-3" />
                                <span className="text-sm text-content-primary-bold">{endDate}</span>
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </>
        )}
      </div>
    </AppLayout>
  );
}
